import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { Button } from '@/components/ui/button';
import {
  TrendingUp,
  ShoppingCart,
  Repeat,
  ChevronLeft,
  Plus,
  Users,
  Bell,
  ShoppingBag,
  Banknote,
} from 'lucide-react';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import type {
  AudienceCampaignMetrics,
  AudienceCustomer,
  AudienceDetails,
} from '@/components/audiences/audience-details-types';
import AudienceDetailsHeaderActions from '@/components/audiences/audience-details-header-actions';
import { AudienceStatMetricCard } from '@/components/audiences/audience-metrics-cards';
import AudienceDetailsSection from '@/components/audiences/audience-details-section';
import AudienceCustomersTable from '@/components/audiences/audience-customers-table';
import { AudienceFilterSection } from '@/components/audiences/audience-filter-section';
import { Separator } from '../ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency } from '@/components/metrics/formatters';

/** Temporarily hidden — set to true to show the audience members table again */
export const SHOW_AUDIENCE_MEMBERS_TABLE = false;

interface AudienceDetailsPresenterProps {
  audience: AudienceDetails;
  donutTargetUsersCount?: number;
  donutTotalUserbaseCount: number;
  isDonutLoading?: boolean;
  campaignMetrics: AudienceCampaignMetrics;
  /** `undefined` while the store currency loads: money shows a skeleton. */
  currency: string | undefined;
  customers: AudienceCustomer[];
  customersTotal: number;
  customersPage: number;
  customersPageSize: number;
  onCustomersPageChange: (page: number) => void;
  isLoadingCustomers?: boolean;
  tenantSlug: string;
  locale: Locales;
  onDeleteConfirm?: () => void | Promise<void>;
  deleteDialogOpen: boolean;
  onDeleteDialogOpenChange: (open: boolean) => void;
  removeMode: 'delete' | 'archive';
  dependentCampaignCount: number;
  isLoadingDependencies?: boolean;
  isDeletingAudience?: boolean;
}

export function AudienceDetailsPresenter({
  audience,
  donutTargetUsersCount,
  donutTotalUserbaseCount,
  isDonutLoading = false,
  campaignMetrics,
  currency,
  customers,
  customersTotal,
  customersPage,
  customersPageSize,
  onCustomersPageChange,
  isLoadingCustomers,
  tenantSlug,
  locale,
  onDeleteConfirm,
  deleteDialogOpen,
  onDeleteDialogOpenChange,
  removeMode,
  dependentCampaignCount,
  isLoadingDependencies,
  isDeletingAudience,
}: AudienceDetailsPresenterProps) {
  const { LL } = useI18nContext();

  return (
    <SidebarLayout className="!p-0">
      <CenteredContentLayout>
        <Header className="flex justify-between w-full" rightGutter>
          <div className="flex flex-col gap-2">
            <PageBreadcrumb
              items={[
                {
                  label: LL.audiences.title(),
                  href: `/${tenantSlug}/audiences`,
                },
                { label: LL.audiences.details.breadcrumbLabel() },
              ]}
            />
            <div className="flex min-w-0 items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                <Link
                  to={`/${tenantSlug}/audiences`}
                  className="shrink-0"
                  aria-label={LL.audiences.title()}
                >
                  <ChevronLeft className="size-6" />
                </Link>
                <h1 className="min-w-0 truncate text-lg tracking-tight sm:text-xl">
                  {audience.name}
                </h1>
              </div>
            </div>
          </div>
          <AudienceDetailsHeaderActions
            audienceName={audience.name}
            deleteDialogOpen={deleteDialogOpen}
            onDeleteDialogOpenChange={onDeleteDialogOpenChange}
            removeMode={removeMode}
            dependentCampaignCount={dependentCampaignCount}
            isLoadingDependencies={isLoadingDependencies}
            onRemoveConfirm={onDeleteConfirm}
            isSubmitting={isDeletingAudience}
          />
        </Header>

        <CenteredContent>
          <div className="flex h-full min-w-0 flex-col gap-8 pb-8 sm:gap-10 sm:pb-12">
            <AudienceFilterSection
              className="entry-fade-in-up entry-delay-0"
              tenantSlug={tenantSlug}
              audienceId={audience.id}
              filterTree={audience.filterTree}
              donutTargetUsersCount={donutTargetUsersCount}
              donutTotalUserbaseCount={donutTotalUserbaseCount}
              isDonutLoading={isDonutLoading}
              locale={locale}
            />

            <AudienceDetailsSection
              icon={Bell}
              title={LL.audiences.details.overview.campaignsTitle()}
              description={LL.audiences.details.overview.campaignsDescription({
                count: audience.sentCampaignsCount,
              })}
              className="mt-4 entry-fade-in entry-delay-2"
              actions={
                <>
                  {/*<Button asChild variant="ghost">
                    <Link
                      to={`/${tenantSlug}/campaigns?audienceId=${audience.id}`}
                    >
                      <ExternalLink data-icon="inline-start" />
                      {LL.audiences.details.overview.viewCampaignsLink()}
                    </Link>
                  </Button>*/}
                  <Button asChild>
                    <Link
                      to={`/${tenantSlug}/campaigns/create?audienceId=${audience.id}`}
                    >
                      <Plus data-icon="inline-start" />
                      {LL.campaigns.createCampaignButton()}
                    </Link>
                  </Button>
                </>
              }
            >
              <div className="grid w-full grid-cols-1 gap-4 min-[480px]:grid-cols-2 lg:grid-cols-3">
                <AudienceStatMetricCard
                  className="entry-fade-in-up entry-delay-3"
                  label={LL.audiences.details.overview.campaignMetrics.averageCtr()}
                  value={new Intl.NumberFormat(locale, {
                    style: 'percent',
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }).format(campaignMetrics.averageCtr)}
                  icon={TrendingUp}
                />
                <AudienceStatMetricCard
                  className="entry-fade-in-up entry-delay-4"
                  label={LL.audiences.details.overview.campaignMetrics.averageConversion()}
                  value={new Intl.NumberFormat(locale, {
                    style: 'percent',
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }).format(campaignMetrics.averageConversionRate)}
                  icon={ShoppingCart}
                />
                <AudienceStatMetricCard
                  className="entry-fade-in-up entry-delay-5"
                  label={LL.audiences.details.overview.campaignMetrics.totalRepurchases()}
                  value={campaignMetrics.totalRepurchases.toLocaleString(
                    locale
                  )}
                  icon={Repeat}
                />
                <AudienceStatMetricCard
                  className="entry-fade-in-up entry-delay-6"
                  label={LL.audiences.details.overview.campaignMetrics.totalFirstPurchases()}
                  value={campaignMetrics.totalFirstPurchases.toLocaleString(
                    locale
                  )}
                  icon={ShoppingBag}
                />
                <AudienceStatMetricCard
                  className="entry-fade-in-up entry-delay-7"
                  label={LL.audiences.details.overview.campaignMetrics.totalRevenue()}
                  value={
                    currency === undefined ? (
                      <Skeleton
                        data-testid="currency-skeleton"
                        className="h-5 w-24"
                      />
                    ) : (
                      formatCurrency(
                        campaignMetrics.totalRevenue,
                        locale,
                        currency
                      )
                    )
                  }
                  icon={Banknote}
                />
              </div>
            </AudienceDetailsSection>

            {SHOW_AUDIENCE_MEMBERS_TABLE && (
              <>
                <Separator />

                <AudienceDetailsSection
                  className="entry-fade-in entry-delay-8"
                  icon={Users}
                  title={LL.audiences.details.customers.tabLabel()}
                  description={LL.audiences.details.customers.description()}
                >
                  <AudienceCustomersTable
                    customers={customers}
                    total={customersTotal}
                    page={customersPage}
                    pageSize={customersPageSize}
                    onPageChange={onCustomersPageChange}
                    isLoading={isLoadingCustomers}
                    locale={locale}
                  />
                </AudienceDetailsSection>
              </>
            )}
          </div>
        </CenteredContent>
      </CenteredContentLayout>
    </SidebarLayout>
  );
}
